import { BulletList, AlertBox, Screenshot, ScreenshotGrid } from '../components/UIComponents';
import { ShieldCheck, ShieldAlert, Activity, Bell, Search, Clock, Settings } from 'lucide-react';

export function FirewallCompliance() {
    return (
        <section className="doc-section scroll-mt-20 mt-12" id="firewall-compliance">
            <div className="mb-3 pb-2 border-b border-white/10">
                <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                    <ShieldCheck className="text-green-400" size={22} />
                    12. Firewall Compliance Auditing
                </h2>
            </div>
            <div className="space-y-4">
                <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                    <h3 className="text-[14px] font-bold text-gray-100 mb-3">12.1 Compliance Scan Coverage</h3>
                    <BulletList items={[
                        'Rule Checks: We look for insecure ANY/ANY allow rules and rules that are no longer used.',
                        'Protocol Risks: We flag outdated management protocols like Telnet or plain HTTP.',
                        'Scoring: Get a clear Pass/Fail grade and an overall compliance percentage.',
                        'Reports: Download executive summaries in PDF or detailed technical data in CSV.',
                        'Scheduling: Set up automatic report delivery to your team.',
                        'Trends: See if your firewall compliance is getting better or worse over time.'
                    ]} />
                </div>
                <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                    <h3 className="text-[14px] font-bold text-gray-100 mb-2 flex items-center gap-2">
                        <Clock size={16} className="text-green-400" /> 12.2 Change Tracking
                    </h3>
                    <p className="text-[13px] text-gray-200 leading-relaxed">
                        Every firewall rule change is tracked with a Before/After diff, user attribution, and exact timestamps.
                    </p>
                </div>
                <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                    <h3 className="text-[14px] font-bold text-gray-100 mb-3">12.3 Compliance Reports</h3>
                    <BulletList items={[
                        'Export compliance reports as PDF executive summaries or CSV technical detail.',
                        'Schedule report delivery to stakeholders on a defined frequency.',
                        'Track historical compliance trends to show improvement or regression over time.'
                    ]} />
                </div>
            </div>
            <Screenshot
                src="/Firewall_Compliance_Auditing.png"
                alt="Firewall Compliance Auditing"
            />
        </section>
    );
}

export function FirewallSIEM() {
    return (
        <section className="doc-section scroll-mt-20 mt-12" id="firewall-siem">
            <div className="mb-3 pb-2 border-b border-white/10">
                <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                    <ShieldAlert className="text-rose-500" size={22} />
                    13. Firewall SIEM
                </h2>
                <p className="text-[14px] text-gray-200 mt-1.5 leading-relaxed">
                    We take all those messy firewall logs, clean them up, and put them in one place so you can easily spot security issues.
                </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-gray-800 to-gray-800/50 border border-white/5 rounded-xl p-5">
                    <h3 className="text-[15px] font-bold text-gray-100 mb-2.5 flex items-center gap-2">
                        <Search size={16} className="text-rose-400" /> 13.1 Log Collection, 13.2 Log Types
                    </h3>
                    <BulletList items={[
                        'Works with Multiple Vendors: We collect logs easily via syslog and APIs.',
                        'Standardization: We rewrite logs from different vendors to all look the same.',
                        'Live Updates: Streams logs instantly as things happen.',
                        'Tenant Isolation: Keeps each client\'s firewall logs completely separate.',
                        'Configurable Storage: Keep logs as long as you need to meet compliance rules.',
                        'Traffic Details: See exactly what got allowed or blocked.',
                        'Threat Details: Track antivirus hits and intrusion attempts.',
                        'VPN Checks: Monitor who is connecting to your VPN.',
                        'Web Filters: See what websites are getting blocked by category.',
                        'Admin Audit: See who changed which rules on the firewall.',
                        'System Logs: Keep an eye on device errors and admin logins.'
                    ]} />
                </div>
                <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                    <h3 className="text-[15px] font-bold text-gray-100 mb-2.5">13.3 to 13.7 Detection, Response, and Reporting</h3>
                    <BulletList items={[
                        'We group related events together so you can spot multi-step attacks easier.',
                        'The system watches for port scans, brute-force logins, sudden drops in traffic, and known bad IPs.',
                        'Dashboards show your current traffic, blocked IPs, active threats, rule hits, and alerts in real time.',
                        'You can search through logs quickly to trace exactly how an alert happened.',
                        'Alerts can go straight to Teams, Slack, Email, WhatsApp, webhooks, or open a support ticket automatically.',
                        'Export reports on security incidents to use as evidence or for long-term planning.'
                    ]} />
                    <AlertBox type="warning" icon={<Activity size={16} />}>
                        <strong>Anomaly Detection:</strong> Flags deviations from normal traffic, such as off-hours logins or abnormal data volumes.
                    </AlertBox>
                </div>
            </div>
        </section>
    );
}

export function EventEngine() {
    return (
        <section className="doc-section scroll-mt-20 mt-12" id="event-engine">
            <div className="mb-3 pb-2 border-b border-white/10">
                <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                    <Activity className="text-violet-400" size={22} />
                    14. Real-Time Event Engine
                </h2>
            </div>
            <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                <p className="text-[14px] text-gray-200 leading-relaxed mb-4">
                    The engine watches data and logs as they arrive and instantly sends alerts if thresholds are broken or statuses change.
                </p>
                <h3 className="text-[14px] font-bold text-gray-100 mb-3">14.1 Event Triggers</h3>
                <BulletList items={[
                    'Alerts can fire for: devices going offline, broken thresholds, stopped services, changed firewall rules, missed compliance checks, broken camera streams, low storage, failed patches, and SIEM threats.',
                    'You also get notified when new devices connect, equipment reboots, a port drops, certificates expire, computers run too hot, or if the AC/generators fail.'
                ]} />
                <h3 className="text-[14px] font-bold text-gray-100 mt-5 mb-3">14.2 Event Record Structure</h3>
                <BulletList items={[
                    'Every event tracks the device name, what happened, the current status, severity level, ticket number, when it started, when it ended, and how long it lasted.',
                    'This makes it easy to review what happened during an incident later on.'
                ]} />
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {['Critical', 'Warning', 'Informational', 'Acknowledged', 'Resolved'].map(status => (
                        <div key={status} className="px-3 py-1.5 bg-black/30 border border-white/5 rounded text-[12px] text-center text-gray-300">
                            {status}
                        </div>
                    ))}
                </div>
            </div>
            <ScreenshotGrid>
                <Screenshot
                    src="/Real_Time_Event_Engine.png"
                    alt="Real Time Event Engine"
                />
                <Screenshot
                    src="/Real_Time_Event_Engine1.png"
                    alt="Real Time Event Engine"
                />
            </ScreenshotGrid>
        </section>
    );
}

export function NotificationRules() {
    return (
        <section className="doc-section scroll-mt-20 mt-12" id="notification-rules">
            <div className="mb-3 pb-2 border-b border-white/10">
                <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                    <Bell className="text-amber-500" size={22} />
                    15. Notification Rules & Alert Management
                </h2>
            </div>
            <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                <h3 className="text-[14px] font-bold text-gray-100 mb-3">15.1 to 15.3 Notification Rules & Workflow</h3>
                <BulletList items={[
                    'Comes with 15 standard rules built-in for common IT monitoring scenarios.',
                    'Custom Rules: Build your own alerts based on client name, severity, or custom tags.',
                    'Send alerts multiple ways: Microsoft Teams, Slack, Email, WhatsApp, and Webhooks.',
                    'Ticketing: Automatically log issues in tools like ServiceDesk or Jira.',
                    'Automation: When an event triggers, it follows your rules, messages the right people, stays open until fixed, records how long it took, and updates the ticket.'
                ]} />
                <h3 className="text-[14px] font-bold text-gray-100 mt-5 mb-3">15.4 Service Monitoring</h3>
                <BulletList items={[
                    'We check your important services every minute to ensure they are running.',
                    'We chart how much CPU, memory, and network traffic your services are using so you can spot trends.',
                    'If a service crashes, we log the host, which service died, the exact time, and its resource usage right before the crash, then notify your team.',
                    'The dashboard gives you a single place to see what\'s running and if anything is using too many resources.'
                ]} />
            </div>
            <Screenshot
                src="/Notification_Rules_&_Alert_Management.png"
                alt="Notification Rules & Alert Management"
            />
        </section>
    );
}

export function EventConfiguration() {
    return (
        <section className="doc-section scroll-mt-20 mt-12" id="event-configuration">
            <div className="mb-3 pb-2 border-b border-white/10">
                <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                    <Activity className="text-pink-500" size={22} />
                    14.5 Event Configuration
                </h2>
                <p className="text-[14px] text-gray-200 mt-1.5 leading-relaxed">
                    Fine-tune how events are generated, evaluated, and categorized to reduce noise and focus on what matters.
                </p>
            </div>
            <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                <h3 className="text-[14px] font-bold text-gray-100 mb-3">14.5.1 Configuration Capabilities</h3>
                <BulletList items={[
                    'Custom Event Definition: Define new custom events based on specific log patterns, metric thresholds, or state changes.',
                    'Threshold Tuning: Configure sensitivity and duration conditions (e.g., only trigger if CPU > 90% for 5 straight minutes).',
                    'Event Enrichment: Automatically attach relevant context, tags, or suggested remediation steps to events when they fire.',
                    'Severity Mapping: Adjust how specific occurrences map to severity levels (Critical, Warning, Info) depending on the affected asset\'s criticality.',
                    'Quiet Periods & Maintenance: Temporarily mute events for specific assets during planned maintenance windows.'
                ]} />
            </div>
            <ScreenshotGrid>
                <Screenshot
                    src="/Event_Configuration_Settings.png"
                    alt="Event Configuration Settings"
                />
                <Screenshot
                    src="/Event_Threshold_Tuning.png"
                    alt="Event Threshold Tuning"
                />
            </ScreenshotGrid>
        </section>
    );
}

export function RuleEngine() {
    return (
        <section className="doc-section scroll-mt-20 mt-12" id="rule-engine">
            <div className="mb-3 pb-2 border-b border-white/10">
                <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                    <Settings className="text-teal-400" size={22} />
                    15.5 Advanced Rule Engine
                </h2>
                <p className="text-[14px] text-gray-200 mt-1.5 leading-relaxed">
                    Build complex, multi-condition logic to automate your responses, trigger playbooks, and route alerts with precision.
                </p>
            </div>
            <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                <h3 className="text-[14px] font-bold text-gray-100 mb-3">15.5.1 Rule Builder Features</h3>
                <BulletList items={[
                    'Visual Rule Builder: Create complex IF/THEN/ELSE logic flows using a simple visual interface without writing code.',
                    'Multi-Condition Triggers: Combine multiple metrics, states, and logs (e.g., IF CPU > 90% AND Memory > 80% AND Ping Fails).',
                    'Automated Actions: Trigger automated scripts, restart services, isolate network ports, or execute custom webhooks.',
                    'Dynamic Routing: Route alerts to different teams or individuals based on time of day, severity, or specific asset tags.',
                    'Simulation Mode: Test your rules against historical data to ensure they trigger correctly before making them live.'
                ]} />
            </div>
            <ScreenshotGrid>
                <Screenshot
                    src="/Rule_Engine_Builder.png"
                    alt="Visual Rule Builder"
                />
                <Screenshot
                    src="/Rule_Engine_Actions.png"
                    alt="Automated Rule Actions"
                />
            </ScreenshotGrid>
        </section>
    );
}
